import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import ModalPortal, { MODAL_Z } from './ModalPortal';

/**
 * Delete confirmation that renders its own trigger chip. Callers that already
 * have a trigger of their own should use ConfirmDialog instead.
 */
export default function DeleteConfirmatationModal({ text, heading = 'Delete', deleteFunction, isProcessing, isSuccess, name = 'Delete' }) {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setShowModal(false);
    }
  }, [isSuccess]);

  const close = () => {
    if (!isProcessing) setShowModal(false);
  };

  const handleDeleteRequest = () => {
    deleteFunction();
    setShowModal(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 transition hover:border-rose-300 hover:bg-rose-100"
      >
        {name}
      </button>

      <ModalPortal isOpen={showModal} onClose={close}>
        <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center p-4`}>
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-heading"
            className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-start gap-4 px-6 pt-6">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <FiAlertTriangle className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 id="delete-modal-heading" className="text-base font-semibold text-slate-900">
                  {heading}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Are you sure you want to delete{' '}
                  <span className="font-medium text-slate-900">{text}</span>? This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                disabled={isProcessing}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button
                type="button"
                onClick={close}
                disabled={isProcessing}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isProcessing) handleDeleteRequest();
                }}
                disabled={isProcessing}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isProcessing ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    </>
  );
}
